import { useState } from 'react'
import { Lock } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { releasePaymentBlock } from '../api/incoming-invoices.api'
import type { IncomingInvoiceDetail } from '../types/incoming-invoices.types'

/**
 * AP-34 Nachtrag 3 (PT-7, E-4) — **die Zahlungssperre, solange die Rechnung noch nicht freigegeben ist.**
 *
 * Die Sperre stand bisher nur im Abschnitt „Zahlung", und den gibt es erst nach der Freigabe. Seit die
 * KI-Lesung „bereits bezahlt" als Sperre vorschlägt, entsteht sie schon im Entwurf — wer sie übernimmt,
 * muss sie sehen und lösen können (Auftraggeber 8.10.2026), sonst erfährt er erst nach dem Abschließen davon.
 *
 * Die Sperre wegen geänderter IBAN entsteht erst bei der Freigabe und wird im Abschnitt „Zahlung" gelöst —
 * sie verlangt eine Entscheidung, die hier nicht hingehört.
 */
export function Zahlungssperre({ rechnung, onRechnung, onFehler }: { rechnung: IncomingInvoiceDetail; onRechnung: (r: IncomingInvoiceDetail) => void; onFehler: (t: string | null) => void }) {
  const user = useAuthStore((s) => s.user)
  const darf = erfuelltRecht('incoming_invoices.payment.manage', user?.permissions)
  const [laeuft, setLaeuft] = useState(false)
  if (!rechnung.paymentBlocked || rechnung.paymentBlockedReason === 'IBAN_CHANGED') return null

  const loesen = async () => {
    try {
      setLaeuft(true)
      onFehler(null)
      onRechnung(await releasePaymentBlock(rechnung.id, {}))
    } catch (e) {
      onFehler(apiErrorText(e, 'Die Sperre ließ sich nicht lösen.'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm">
      <Lock size={15} className="shrink-0 text-amber-700 dark:text-amber-300" />
      <span className="min-w-0 flex-1">
        <strong>Zahlung gesperrt</strong>
        {rechnung.paymentBlockedReason ? ` — ${rechnung.paymentBlockedReason}.` : '.'} Die Rechnung kommt in keinen Zahlungslauf, bis die Sperre gelöst ist.
      </span>
      {darf ? (
        <button type="button" className="rounded-md border border-border bg-background px-3 py-1 hover:bg-muted disabled:opacity-50" disabled={laeuft} onClick={() => void loesen()}>
          Sperre lösen
        </button>
      ) : (
        <span className="text-muted-foreground">Lösen kann sie, wer Zahlungen verwalten darf.</span>
      )}
    </div>
  )
}
